<template>
  <button
    v-if="count > 0"
    type="button"
    class="decisions-banner"
    :aria-label="ariaLabel"
    @click="open"
  >
    <span class="decisions-banner__icon" aria-hidden="true">
      <f7-icon f7="bell_fill" size="18"></f7-icon>
    </span>
    <span class="decisions-banner__title">{{ title }}</span>
    <!-- A count beside a NAMED item is noise — the title already says there is
         exactly one, and which. -->
    <span v-if="!deepLinkId" class="decisions-banner__count">{{ count }}</span>
    <f7-icon f7="chevron_left" size="16" class="decisions-banner__chev"></f7-icon>
  </button>
</template>

<script setup>
/**
 * DecisionsInboxBanner — a lightweight, store-derived entry to the "قرارات
 * مطلوبة" hub, dropped into a surface where the member already sees their order
 * (OrdersPage). Reactive off the pinia-orm OrderBoxItem store — it lights up the
 * moment any loaded item is awaiting the member's decision, self-hides at zero,
 * and costs no extra fetch.
 *
 * COUNT SEMANTICS (deliberate, not a bug):
 *   This count is a HINT, not an authority. It counts only OrderBoxItems already
 *   in the pinia-orm store (typically the box the member is viewing on
 *   OrdersPage) — so it can undercount decisions in boxes not yet loaded this
 *   session. It reacts to the optimistic badge-clear on answer (useItemIssue.
 *   clearItemBadge), so it drops the instant the member acts. The AUTHORITATIVE
 *   count is the inbox page (useDecisionsInbox), which since interaction-v2 reads
 *   `GET /item-issues` directly — one call, member-scoped server-side.
 *
 *   We keep the banner store-derived on purpose, and that choice is now a real
 *   trade rather than a workaround: an index route DOES exist, so making this
 *   count authoritative is possible — it would just cost a request on every
 *   OrdersPage view to correct a supplementary hint whose PRIMARY entry points
 *   (the per-item badge and the push notification) are already exact. Tapping the
 *   banner opens the hub, which then shows the authoritative list either way. If
 *   the undercount ever bites, the fix is `useDecisionsInbox().ensureLoaded()`
 *   here — not a new backend endpoint.
 */
import { useRepo } from 'pinia-orm';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useIssueNavigation } from '@/composables/useIssueNavigation.js';
import OrderBoxItem from '@/models/commerce/OrderBoxItem.js';
import { itemDisplayName } from '@/utils/orderBoxItemDisplay.js';

const { t } = useI18n({ useScope: 'global' });
const { openInbox, openIssue } = useIssueNavigation();

// Gate on BOTH awaitingMember AND a present id — the same predicate the per-item
// chips (ItemCard / BoxTotalsBreakdown) and the inbox scan (useDecisionsInbox)
// use. Without the id check the banner could count an awaiting-but-idless item
// that renders no chip on any row (a phantom hint with no matching affordance);
// keeping the gate identical everywhere means the count never disagrees with
// what the member can actually see and tap.
const awaiting = computed(() =>
  useRepo(OrderBoxItem)
    .all()
    .filter((it) => it?.activeIssue?.awaitingMember && it?.activeIssue?.id != null),
);

const count = computed(() => awaiting.value.length);

/**
 * The issue to open DIRECTLY, or null to open the list.
 *
 * A list of one is a screen that asks her to tap the only thing on it — this is
 * the row that takes tabbar → answered from 5 taps to 3. Two or more is
 * unchanged: the inbox is where a choice between them belongs.
 *
 * `issueRoute` is a pure string builder, so an absent id would navigate to a
 * path containing the literal "null" and dead-end on a 404. The id gate in
 * `awaiting` already prevents that; this stays explicit because the consequence
 * of it silently regressing is a dead tap. Falling back to the inbox loses
 * nothing — it is the same destination the banner has always had.
 */
const deepLinkId = computed(() => {
  if (count.value !== 1) return null;
  const id = awaiting.value[0]?.activeIssue?.id;
  return id != null ? id : null;
});

const title = computed(() =>
  deepLinkId.value
    ? t('collection.inbox.banner.single', { item: itemDisplayName(awaiting.value[0]) })
    : t('collection.inbox.header'),
);

const ariaLabel = computed(() =>
  deepLinkId.value ? title.value : `${t('collection.inbox.header')}: ${count.value}`,
);

function open() {
  if (deepLinkId.value != null) {
    openIssue(deepLinkId.value);
    return;
  }
  openInbox();
}
</script>

<style scoped>
.decisions-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 52px;
  margin: 0 0 12px;
  padding: 10px 14px;
  border: 1px solid var(--ci-await-ring, rgba(201, 130, 10, 0.42));
  border-radius: var(--poolit-radius-lg, 16px);
  background: var(--ci-await-bg, #fbefd4);
  color: var(--ci-await-fg, #b45309);
  text-align: start;
  cursor: pointer;
  transition: filter var(--poolit-transition-fast, 150ms) ease;
}

.decisions-banner:active {
  filter: brightness(0.97);
}

.decisions-banner:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--ci-await-ring, rgba(201, 130, 10, 0.5));
}

.decisions-banner__icon {
  flex-shrink: 0;
  display: grid;
  place-items: center;
}

.decisions-banner__title {
  flex: 1;
  min-width: 0;
  font-weight: var(--poolit-font-weight-bold, 700);
  font-size: var(--poolit-font-size-body, 0.92rem);
}

.decisions-banner__count {
  flex-shrink: 0;
  min-width: 22px;
  height: 22px;
  padding: 0 7px;
  border-radius: var(--poolit-radius-full, 999px);
  background: var(--ci-await-fg, #b45309);
  color: #fff;
  font-size: var(--poolit-font-size-small, 0.75rem);
  font-weight: var(--poolit-font-weight-bold, 700);
  display: inline-grid;
  place-items: center;
  line-height: 1;
}

.decisions-banner__chev {
  flex-shrink: 0;
  opacity: 0.7;
}
</style>
